import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getCompanyList } from '@/api/modules/company'
import { parseCompanyListFromResponse, COMPANY_PERM, permissionsAllowScriptOnline } from '@/utils/companyPermissions'
import { useUserStore } from '@/store/modules/user'

/**
 * 根据 GET /api/companies 返回的各公司 permissions，汇总当前用户是否具备某项公司能力（用于菜单与路由，与 profile 里 user.permissions 解耦）
 */
export const useCompanyAccessStore = defineStore('companyAccess', () => {
  const loaded = ref(false)
  const loading = ref(false)
  const hasScriptOnline = ref(false)
  const hasVoiceGeneration = ref(false)
  const hasAiAudiobookGeneration = ref(false)
  const hasVideoDramaRecording = ref(false)

  function aggregateFromCompanies (list) {
    let script = false
    let voice = false
    let audiobook = false
    let drama = false
    for (const c of list) {
      const p = Array.isArray(c.permissions) ? c.permissions : []
      if (p.includes(COMPANY_PERM.SCRIPT_ONLINE)) script = true
      if (p.includes(COMPANY_PERM.VOICE_GENERATION)) voice = true
      if (p.includes(COMPANY_PERM.AI_AUDIOBOOK_GENERATION)) audiobook = true
      if (p.includes(COMPANY_PERM.VIDEO_DRAMA_RECORDING)) drama = true
    }
    hasScriptOnline.value = script
    hasVoiceGeneration.value = voice
    hasAiAudiobookGeneration.value = audiobook
    hasVideoDramaRecording.value = drama
  }

  async function refresh () {
    loading.value = true
    try {
      const res = await getCompanyList({ pageSize: 500, _t: Date.now() })
      const list = parseCompanyListFromResponse(res)
      aggregateFromCompanies(list)
    } catch {
      hasScriptOnline.value = false
      hasVoiceGeneration.value = false
      hasAiAudiobookGeneration.value = false
      hasVideoDramaRecording.value = false
    } finally {
      loaded.value = true
      loading.value = false
    }
    try {
      sessionStorage.setItem(
        'companyAccess:videoDrama',
        hasVideoDramaRecording.value ? '1' : '0'
      )
    } catch {
      /* 忽略 */
    }
  }

  let inFlight = null
  async function ensureLoaded () {
    if (loaded.value) return
    if (inFlight) return inFlight
    inFlight = refresh().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  /** @param {string} permission 如 company:voice:generation */
  function hasCompanyPermission (permission) {
    const map = {
      [COMPANY_PERM.SCRIPT_ONLINE]: () => hasScriptOnline.value,
      [COMPANY_PERM.VOICE_GENERATION]: () => hasVoiceGeneration.value,
      [COMPANY_PERM.AI_AUDIOBOOK_GENERATION]: () => hasAiAudiobookGeneration.value,
      [COMPANY_PERM.VIDEO_DRAMA_RECORDING]: () => hasVideoDramaRecording.value
    }
    const fn = map[permission]
    return fn ? fn() : false
  }

  return {
    loaded,
    loading,
    hasScriptOnline,
    hasVoiceGeneration,
    hasAiAudiobookGeneration,
    hasVideoDramaRecording,
    refresh,
    ensureLoaded,
    hasCompanyPermission
  }
})
